import { api } from '../api/client';

import { API_ITEM_COMMENTS_URL, API_ITEM_COMMENT_URL, API_ITEM_COMMENT_HISTORY_URL } from '../utils';

/**
 * COMMENTS ON AN ITEM (KPE-005, KPR-306; keepr-api SCHEMA.md § Comments on
 * items, utils/itemComments.js). The journal that replaces a card's notes
 * section: people who can edit an item add dated, attributed comments;
 * everyone edits and deletes only their own; an edited comment's history is
 * readable. All six routes are session-only.
 *
 * - `GET /api/items/{id}/comments?skip&limit` → `{ comments, total, skip,
 *   limit, strictAuditing, canComment }`, oldest first. `canComment` is the
 *   POST's own verdict for the caller; `strictAuditing` says whether a deleted
 *   comment is listed as a placeholder (`deleted: true`, who and when, no
 *   body). Each comment carries `mine`.
 * - `POST …/comments { body }` → 201, the comment.
 * - `GET …/comments/{commentId}` → the comment; a deleted one (strict
 *   auditing on) carries its last `body`.
 * - `PATCH …/comments/{commentId} { body }` → the comment (author only).
 * - `DELETE …/comments/{commentId}?strictAuditing=true|false` → the comment,
 *   `deleted: true`, no body (author only; a soft delete). The query
 *   parameter is REQUIRED (KPR-789): the strict-auditing mode the person was
 *   shown, i.e. the list's own `strictAuditing`. When it is not the card's
 *   mode at that moment the API writes nothing and answers 409
 *   `comment_audit_changed` with the current `strictAuditing`.
 * - `GET …/comments/{commentId}/history` → `{ comment, versions: [{ body, at,
 *   current }] }`, oldest first.
 *
 * Refusals keep `error.response.data` (`code`, `limit`, `length`) for the
 * caller to word (utils/commentRefusals.js): 404 (the item cannot be read) ·
 * 409 `comments_off` · 403 `comments_need_write` · `collection_archived` ·
 * `comment_not_author` · 404 `comment_not_found` · 409 `comment_deleted` ·
 * 409 `comment_audit_changed` (a delete only) ·
 * `comment_edit_limit` · `comment_conflict` · 400 `comment_blank` /
 * `comment_invalid` / `comment_too_long` · 429.
 */

/** The page size the list asks for (the API's default; at most 200). */
export const COMMENTS_PAGE_SIZE = 50;

/** One page of an item's comments, oldest first. */
export async function listItemComments(itemId, { skip = 0, limit = COMMENTS_PAGE_SIZE } = {}) {
	const { data } = await api.get(API_ITEM_COMMENTS_URL(itemId), { params: { skip, limit } });
	return {
		comments: Array.isArray(data?.comments) ? data.comments : [],
		total: Number.isFinite(data?.total) ? data.total : 0,
		skip: Number.isFinite(data?.skip) ? data.skip : skip,
		limit: Number.isFinite(data?.limit) ? data.limit : limit,
		strictAuditing: data?.strictAuditing === true,
		canComment: data?.canComment === true,
	};
}

/** Add a comment as the caller. */
export async function addItemComment(itemId, body) {
	const { data } = await api.post(API_ITEM_COMMENTS_URL(itemId), { body });
	return data || {};
}

/** One comment (a deleted one, under strict auditing, with its last text). */
export async function getItemComment(itemId, commentId) {
	const { data } = await api.get(API_ITEM_COMMENT_URL(itemId, commentId));
	return data || {};
}

/** Replace the text of your own comment; the old text goes to its history. */
export async function editItemComment(itemId, commentId, body) {
	const { data } = await api.patch(API_ITEM_COMMENT_URL(itemId, commentId), { body });
	return data || {};
}

/**
 * Delete your own comment (soft: the API keeps it). `strictAuditing` is the
 * mode the person was shown (the list's), sent on EVERY delete: a mismatch
 * with the card's mode now is refused 409 `comment_audit_changed`, nothing
 * written (KPR-789).
 */
export async function deleteItemComment(itemId, commentId, strictAuditing) {
	const { data } = await api.delete(API_ITEM_COMMENT_URL(itemId, commentId), {
		params: { strictAuditing: strictAuditing === true },
	});
	return data || {};
}

/** Every version of a comment, oldest first; the last is the current text. */
export async function getItemCommentHistory(itemId, commentId) {
	const { data } = await api.get(API_ITEM_COMMENT_HISTORY_URL(itemId, commentId));
	return {
		comment: data?.comment || null,
		versions: Array.isArray(data?.versions) ? data.versions : [],
	};
}
